import { useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import { joinLocalized } from "@/lib/formatting/list";
import { NewSoonBadge } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  WALLET_PROVIDERS,
  type WalletProviderValue,
} from "@/modules/onboarding/constants/wallet-providers";
import { MethodCard } from "@/modules/settings/components/redesign/store-settings/payment/MethodCard";
import { WalletFields } from "@/modules/settings/components/redesign/store-settings/payment/WalletFields";
import { InstaPayFields } from "@/modules/settings/components/redesign/store-settings/payment/InstaPayFields";
import {
  CardLogo,
  CodLogo,
  InstaPayLogo,
  WalletCluster,
} from "@/modules/settings/components/redesign/store-settings/payment/payment-logos";
import {
  hasOnlineEnabled,
  isInstaConfigured,
  isWalletConfigured,
  PHONE_RE,
  type InstaState,
  type PanelForm,
} from "@/modules/settings/components/redesign/store-settings/payment/payment-form";

const K = keys.quickSetup;

/**
 * Quick-setup payment step. Reuses the exact Settings payment building blocks
 * (MethodCard + WalletFields + InstaPayFields + payment-form helpers) so the two
 * surfaces stay in lockstep: COD (+ auto-confirm), multi-provider Wallet,
 * InstaPay (phone / IPA / bank account / card), and a Card "coming soon" teaser.
 * No standalone bank tile — bank is an InstaPay receiver, mirroring Settings.
 */
export function WizardPaymentStep({
  form,
  onChange,
  onDone,
  minLabel,
}: {
  form: PanelForm;
  onChange: (next: PanelForm) => void;
  onDone: () => void;
  minLabel?: string;
}): ReactElement {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);

  const patch = (next: Partial<PanelForm>): void => onChange({ ...form, ...next });

  const toggleWalletProvider = (provider: WalletProviderValue): void => {
    const has = form.wallets.some((w) => w.provider === provider);
    patch({
      wallets: has
        ? form.wallets.filter((w) => w.provider !== provider)
        : [...form.wallets, { provider, phone: "", prefilled: false }],
    });
  };
  const setWalletPhone = (provider: WalletProviderValue, phone: string): void =>
    patch({
      wallets: form.wallets.map((w) =>
        w.provider === provider ? { ...w, phone, prefilled: false } : w,
      ),
    });

  // Same validation rules as the Settings panel, plus the always-enabled
  // button's own "at least one method" check (previously a disabled state).
  const validate = (): string | null => {
    if (!form.cod && !hasOnlineEnabled(form)) return minLabel ?? t(K.errors.sellType);
    if (form.walletOn) {
      if (form.wallets.length === 0) return t(keys.onboarding.paymentWalletSelectOne);
      for (const w of form.wallets) {
        if (!PHONE_RE.test(w.phone.trim()))
          return t(keys.onboarding.paymentWalletInvalidPhone);
      }
    }
    if (form.instaOn) {
      const i = form.insta;
      if (i.receiver === "PHONE" && !PHONE_RE.test(i.phone.trim()))
        return t(keys.onboarding.paymentInstaPayInvalidPhone);
      if (i.receiver === "IPA" && i.ipa.trim().length < 3)
        return t(keys.onboarding.paymentInstaPayEnterIPA);
      if (i.receiver === "BANK_ACCOUNT") {
        if (!i.bankName.trim()) return t(keys.onboarding.paymentBankSelectError);
        if (!i.holder.trim()) return t(keys.onboarding.paymentBankHolderError);
        if (i.account.trim().length < 5) return t(keys.onboarding.paymentBankAccountError);
      }
      if (i.receiver === "CARD") {
        if (i.cardNumber.trim().length < 6) return t(keys.errors.required);
        if (!i.cardHolder.trim()) return t(keys.errors.required);
      }
    }
    return null;
  };

  // At least one usable method: COD, or an online method that's switched on.
  const anySelected = form.cod || hasOnlineEnabled(form);

  const handleDone = (): void => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onDone();
  };

  const card: CSSProperties = {
    animation: "qs-in .3s ease",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 18,
    boxShadow: "0 1px 2px rgba(15,20,40,0.04)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  };
  const groupLabel: CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--text-3)",
  };

  return (
    <div style={card}>
      {/* Receive payment */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={groupLabel}>{t(keys.seller.pmGroupReceive)}</span>
        <MethodCard
          logo={<CodLogo />}
          title={t(keys.seller.codTitle)}
          helper={t(keys.seller.codHelper)}
          checked={form.cod}
          onToggle={(cod) => patch({ cod })}
          trailing={
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.4,
                color: "var(--gold-ink)",
                background: "var(--sel-bg)",
                borderRadius: 999,
                padding: "4px 9px",
                whiteSpace: "nowrap",
              }}
            >
              {t(K.payments.codRecommended)}
            </span>
          }
        />
        {form.cod ? (
          <MethodCard
            logo={<CodLogo />}
            title={t(keys.seller.storeSettings.autoConfirmCodTitle)}
            helper={t(keys.seller.storeSettings.autoConfirmCodHelper)}
            checked={form.autoConfirmCod}
            onToggle={(autoConfirmCod) => patch({ autoConfirmCod })}
          />
        ) : null}
      </div>

      {/* Online payment */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={groupLabel}>{t(keys.seller.pmGroupOnline)}</span>
        <MethodCard
          logo={<WalletCluster active={form.walletOn} />}
          title={t(keys.paymentOptions.typeWallet)}
          helper={t(keys.seller.pmWalletsHelper)}
          checked={form.walletOn}
          onToggle={(walletOn) => patch({ walletOn })}
        >
          <WalletFields
            wallets={form.wallets}
            onToggleProvider={toggleWalletProvider}
            onPhoneChange={setWalletPhone}
          />
        </MethodCard>

        <MethodCard
          logo={<InstaPayLogo active={form.instaOn} />}
          title={t(keys.paymentOptions.typeInstapay)}
          checked={form.instaOn}
          onToggle={(instaOn) => patch({ instaOn })}
        >
          <InstaPayFields
            value={form.insta}
            onChange={(insta: InstaState) => patch({ insta })}
          />
        </MethodCard>

        <MethodCard
          logo={<CardLogo />}
          title={t(keys.seller.pmCardComingTitle)}
          helper={t(keys.seller.pmCardComingHelper)}
          disabled
          trailing={<NewSoonBadge label={t(K.payments.cardSoon)} />}
        />
      </div>

      {error ? (
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--danger)" }}>{error}</div>
      ) : null}

      <button
        onClick={handleDone}
        style={{
          width: "100%",
          padding: "13px 16px",
          borderRadius: 10,
          border: "none",
          background: "var(--accent)",
          color: "var(--navy)",
          fontSize: 16,
          fontWeight: 800,
          cursor: "pointer",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        {form.cod && !hasOnlineEnabled(form) ? t(K.payments.continueCod) : anySelected ? t(K.logo.continue) : minLabel}
      </button>
    </div>
  );
}

/** Build the buyer-facing payment summary line shown on the Done screen. */
export function paymentSummary(
  form: PanelForm,
  t: ReturnType<typeof useTranslations>,
  locale: string,
): string {
  const labels: string[] = [];
  if (form.cod) labels.push(t(keys.seller.codTitle));
  if (form.walletOn && isWalletConfigured(form.wallets)) {
    for (const w of form.wallets) {
      const p = WALLET_PROVIDERS.find((x) => x.value === w.provider);
      labels.push((locale === "ar" ? p?.labelAr : p?.labelEn) ?? w.provider);
    }
  }
  if (form.instaOn && isInstaConfigured(form.insta)) labels.push(t(keys.paymentOptions.typeInstapay));
  return joinLocalized(labels, locale);
}
